import type {DatevPaymentsMapping} from 'app/modules/admin/booking/features/payments/models/datevPaymentsMapping';
import {round2} from '@vat/engine/rounding';
import type {BookingLite, ExpenseLite, LandlordTaxProfile, VatLine, VatRate} from '@vat/engine/types';
import {VAT_OBJEKT_MODEL_COPY} from '@vat/vat-copy';
import type {VatExportLandlord, VatExportPropertyLabel} from './vat-export.types';
import {kontoLabel} from './vat-konto-labels';

/**
 * Reines Modell der „Umsatzsteuerabrechnung je Objekt“ (Spec 2.2, Task C0): Erträge und Kosten je Konto × Steuersatz,
 * anrechenbare Vorsteuer, Block „nicht steuerbar“ und Saldo (USt-Zahllast je Objekt). Keine Firestore-, PDF- oder
 * Excel-Abhängigkeit – Ausgabe übernimmt Task C1, Datenbeschaffung Task C2.
 *
 * Entscheidungen (Plan lässt offen):
 * - Zeilen-Schlüssel je Konto UND Label: die Wohnraum-Warmmiete ohne `divisionWarm` steht als eigene Zeile
 *   „Warmmiete (ohne Aufteilung)“ neben den aufgeteilten Beständen desselben Kontos.
 * - Kosten = EXPENSE-Zeilen mit Kz 66 (Regel VORSTEUER sowie Vorsteuer-Nachträge per ADJUSTMENT), damit der Saldo
 *   mit Kz 66 der Voranmeldung übereinstimmt. Je Konto entstehen mehrere Zeilen, wenn der Anrechenbar-Modus abweicht.
 *   Nachträge § 17 stehen als eigene Zeile „Nachtrag § 17“ mit den Zeilenwerten (Delta) – der Rechnungsbetrag zählt nur
 *   in der VORSTEUER-Zeile. Ertrags-Korrekturen (ADJUSTMENT auf Buchung/Zahlung) laufen ohnehin mit Zeilenwerten.
 * - Kosten-Konto = `expense.accountRefID` (Firestore-Ref, kein SKR-Konto) mit `accountLabel`; ohne Konto „–“ /
 *   „Ohne Buchungskonto“. Die Konto-Felder liefert Task B1 (bis dahin strukturell über `ObjektExpenseLite`).
 *
 * @author Anthony Nahas
 * @since 13.09.26
 */
export interface ObjektKontoRow {
    konto: string;
    label: string;
    brutto: number;
    netto: number;
    ust: number;
    anrechenbarLabel: string;
    anrechenbareUst: number;
    lineCount: number;
}

export interface ObjektRateGroup {
    rate: VatRate;
    rows: ObjektKontoRow[];
    sum: Pick<ObjektKontoRow, 'brutto' | 'netto' | 'ust' | 'anrechenbareUst'>;
}

export interface ObjektAreaMonth {
    monthKey: string;
    totalArea: number;
    taxableArea: number;
    share: number;
    mode: string;
}

export interface ObjektUnitRow {
    unitID: string;
    label: string;
    categoryType: string | null;
    tenantsName: string | null;
    area: number | null;
    taxable: boolean;
    vacant: boolean;
}

/** ExpenseLite plus Konto-Felder aus Task B1 (`nahausBookingsAccount`), bis `types.ts` sie trägt. */
export type ObjektExpenseLite = ExpenseLite & {
    accountRefID?: string | null;
    accountLabel?: string | null;
    accountType?: string | null;
};

export interface ObjektAbrechnungPeriod {
    year: number;
    periodKey: string | null;                // null = Kalenderjahr
    label: string;
    start: Date;
    end: Date;
}

export interface ObjektAbrechnungInput {
    landlord: VatExportLandlord;
    taxProfile: LandlordTaxProfile;
    property: VatExportPropertyLabel;
    period: ObjektAbrechnungPeriod;
    lines: VatLine[];                        // nur Zeilen dieser Immobilie (source.propertyID) – fremde werden ignoriert
    bookingsByID: Record<string, BookingLite>;
    paymentBookingIDs: Record<string, string>;   // paymentID → bookingID (PAYMENT-Zeilen ohne PaymentLite auflösen)
    expensesByID: Record<string, ObjektExpenseLite>;
    kontoMapping: DatevPaymentsMapping;      // Kontonummern (kundenspezifisch gespeichert, sonst SKR03-Default)
    areaMonths: ObjektAreaMonth[];
    units: ObjektUnitRow[];                  // Einheiten zum letzten Monat
    engineVersion: string;
    now: Date;
}

export interface ObjektAbrechnungModel {
    ertraege: ObjektRateGroup[];
    kosten: ObjektRateGroup[];
    nichtSteuerbar: ObjektKontoRow[];
    totals: { ertraegeUst: number; kostenAnrechenbar: number; saldo: number };
}

export type ErtragsKomponente = 'kalt' | 'bk' | 'hk' | 'sonstige' | 'kaution' | 'mahn' | 'zins';

const RATE_ORDER: ReadonlyArray<VatRate> = [0, 7, 19];

const toCents = (value: number): number => Math.round(round2(value) * 100);

/** Verteilt `total` centgenau nach `weights`; Rundungsrest (und alles bei Gewichtssumme ≤ 0) landet auf Index 0. */
export const splitByProportions = (total: number, weights: number[]): number[] => {
    if (weights.length === 0) {
        return [round2(total)];
    }
    const totalCents = toCents(total);
    const safeWeights = weights.map(w => (Number.isFinite(w) && w > 0 ? w : 0));
    const weightSum = safeWeights.reduce((a, b) => a + b, 0);
    if (weightSum <= 0) {
        return safeWeights.map((_, i) => (i === 0 ? totalCents / 100 : 0));
    }
    const cents = safeWeights.map((w, i) => (i === 0 ? 0 : Math.round(totalCents * w / weightSum)));
    cents[0] = totalCents - cents.slice(1).reduce((a, b) => a + b, 0);
    return cents.map(c => c / 100);
};

/** Erlöskonto je Bestandteil aus der kundenspezifischen DATEV-Schablone. */
export const ertragsKonto = (mapping: DatevPaymentsMapping, wohnraum: boolean, komponente: ErtragsKomponente): string => {
    switch (komponente) {
        case 'kalt':
        case 'bk':
        case 'hk':
            return (wohnraum ? mapping.wohnraum : mapping.gewerbe)[komponente];
        case 'kaution':
            return mapping.kaution;
        case 'mahn':
            return mapping.dunningFees;
        case 'zins':
            return mapping.interest;
        default:
            return mapping.fallback;
    }
};

const percentDE = (share: number): string => `${String(round2(share * 100)).replace('.', ',')} %`;

/**
 * Anrechenbar-Text einer Kostenzeile aus dem Engine-Label („… – Vorsteuer 19 %, <Modus>“); nicht ableitbar → Prozent aus share.
 */
const parsePercentDE = (text: string): number | null => {
    const match = /([\d.]+(?:,\d+)?)\s*%/.exec(text);
    if (!match) {
        return null;
    }
    const value = Number(match[1].replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(value) ? value / 100 : null;
};

/**
 * Nach einem Eigentümerwechsel (R8 `rescaleLine`) trägt die Zeile share = Vorsteuer-Anteil × Eigentümeranteil, das
 * Label aber weiter nur den Vorsteuer-Modus – dann wird der effektive Anteil ausgewiesen (Toleranz 0,01 %-Punkte).
 */
const withOwnerShare = (base: string, labelShare: number | null, line: VatLine): string =>
    labelShare !== null && Math.abs(labelShare - line.share) > 0.0001
        ? VAT_OBJEKT_MODEL_COPY.anrechenbar.anteilig(base, percentDE(line.share))
        : base;

const anrechenbarLabelFor = (line: VatLine): string => {
    const copy = VAT_OBJEKT_MODEL_COPY.anrechenbar;
    const tail = line.label.split(' – ').pop() ?? '';
    const modeText = /^Vorsteuer \d+ %, (.+)$/.exec(tail)?.[1]?.trim() ?? null;
    if (modeText) {
        if (modeText === 'voll abziehbar') {
            return withOwnerShare(copy.voll, 1, line);
        }
        if (modeText === 'kein Abzug') {
            return withOwnerShare(copy.kein, 0, line);
        }
        const manuell = /^manueller Schlüssel (.+)$/.exec(modeText);
        if (manuell) {
            return withOwnerShare(copy.prozent(manuell[1]), parsePercentDE(manuell[1]), line);
        }
        const direkt = /^direkte Zurechnung (.+)$/.exec(modeText);
        if (direkt) {
            return withOwnerShare(copy.direkt(direkt[1]), parsePercentDE(direkt[1]), line);
        }
        const korrigiert = /^manuell korrigiert (.+)$/.exec(modeText);
        if (korrigiert) {
            return withOwnerShare(copy.korrigiert(korrigiert[1]), parsePercentDE(korrigiert[1]), line);
        }
        const flaeche = /^Flächenschlüssel.*?([\d.,]+ %)$/.exec(modeText);
        if (flaeche) {
            return withOwnerShare(copy.flaeche(flaeche[1]), parsePercentDE(flaeche[1]), line);
        }
    }
    return copy.prozent(percentDE(line.share));
};

interface RowAccumulator {
    konto: string;
    label: string;
    anrechenbarLabel: string;
    brutto: number;
    netto: number;
    ust: number;
    anrechenbareUst: number;
    lineCount: number;
}

const addTo = (map: Map<string, RowAccumulator>, key: string, seed: Pick<RowAccumulator, 'konto' | 'label' | 'anrechenbarLabel'>, brutto: number, netto: number, ust: number, anrechenbareUst: number): void => {
    const acc = map.get(key) ?? {...seed, brutto: 0, netto: 0, ust: 0, anrechenbareUst: 0, lineCount: 0};
    acc.brutto += brutto;
    acc.netto += netto;
    acc.ust += ust;
    acc.anrechenbareUst += anrechenbareUst;
    acc.lineCount += 1;
    map.set(key, acc);
};

const toRow = (acc: RowAccumulator): ObjektKontoRow => ({
    konto: acc.konto,
    label: acc.label,
    brutto: round2(acc.brutto),
    netto: round2(acc.netto),
    ust: round2(acc.ust),
    anrechenbarLabel: acc.anrechenbarLabel,
    anrechenbareUst: round2(acc.anrechenbareUst),
    lineCount: acc.lineCount,
});

const compareRows = (a: ObjektKontoRow, b: ObjektKontoRow): number =>
    a.konto.localeCompare(b.konto, 'de') || a.label.localeCompare(b.label, 'de') || a.anrechenbarLabel.localeCompare(b.anrechenbarLabel, 'de');

const toRateGroups = (byRate: Map<VatRate, Map<string, RowAccumulator>>): ObjektRateGroup[] =>
    RATE_ORDER
        .filter(rate => (byRate.get(rate)?.size ?? 0) > 0)
        .map(rate => {
            const rows = Array.from(byRate.get(rate)!.values()).map(toRow).sort(compareRows);
            return {
                rate,
                rows,
                sum: {
                    brutto: round2(rows.reduce((a, r) => a + r.brutto, 0)),
                    netto: round2(rows.reduce((a, r) => a + r.netto, 0)),
                    ust: round2(rows.reduce((a, r) => a + r.ust, 0)),
                    anrechenbareUst: round2(rows.reduce((a, r) => a + r.anrechenbareUst, 0)),
                },
            };
        });

const rateMap = <T>(byRate: Map<VatRate, Map<string, T>>, rate: VatRate): Map<string, T> => {
    const existing = byRate.get(rate);
    if (existing) {
        return existing;
    }
    const created = new Map<string, T>();
    byRate.set(rate, created);
    return created;
};

const komponenteForArt = (booking: BookingLite | null): ErtragsKomponente => {
    switch (booking?.art ?? null) {
        case 'WARM_MIETE':
        case 'KALT_MIETE':
            return 'kalt';
        case 'BETRIEBSKOSTEN':
        case 'GUTHABEN':
            return 'bk';
        case 'KAUTION':
            return 'kaution';
        case 'DUNNING_FEES':
            return 'mahn';
        case 'INTEREST_EXPENSES':
            return 'zins';
        default:
            return 'sonstige';
    }
};

const resolveBooking = (input: ObjektAbrechnungInput, line: VatLine): BookingLite | null => {
    if (line.source.type === 'BOOKING') {
        return input.bookingsByID[line.source.id] ?? null;
    }
    const bookingID = input.paymentBookingIDs?.[line.source.id] ?? null;
    return bookingID ? input.bookingsByID[bookingID] ?? null : null;
};

const isRevenueLine = (line: VatLine): boolean =>
    (line.source.type === 'BOOKING' || line.source.type === 'PAYMENT') && line.kz !== 'NONE';

const isCostLine = (line: VatLine): boolean => line.source.type === 'EXPENSE' && line.kz === '66';

const isNonTaxableLine = (line: VatLine): boolean => line.kz === 'NONE' && line.rule !== 'KU_ALTUMSATZ';

const addRevenue = (byRate: Map<VatRate, Map<string, RowAccumulator>>, input: ObjektAbrechnungInput, line: VatLine): void => {
    const map = rateMap(byRate, line.rate);
    const booking = resolveBooking(input, line);
    const wohnraum = line.category === 'STEUERFREI_4_12';
    const anrechenbarLabel = VAT_OBJEKT_MODEL_COPY.anrechenbar.ertrag;

    const division = booking?.art === 'WARM_MIETE' ? booking.divisionWarm : null;
    const weights = division ? [division.kalt ?? 0, division.bk ?? 0, division.hk ?? 0].map(w => Math.max(0, Number(w) || 0)) : [];
    const weightSum = weights.reduce((a, b) => a + b, 0);

    if (division && weightSum > 0) {
        const komponenten: ErtragsKomponente[] = ['kalt', 'bk', 'hk'];
        const brutto = splitByProportions(line.gross, weights);
        const netto = splitByProportions(line.net, weights);
        const ust = splitByProportions(line.tax, weights);
        komponenten.forEach((komponente, i) => {
            if (weights[i] <= 0) {
                return;
            }
            const konto = ertragsKonto(input.kontoMapping, wohnraum, komponente);
            const label = kontoLabel(konto);
            addTo(map, `${konto}|${label}`, {konto, label, anrechenbarLabel}, brutto[i], netto[i], ust[i], ust[i]);
        });
        return;
    }

    const komponente = komponenteForArt(booking);
    const konto = ertragsKonto(input.kontoMapping, wohnraum, komponente);
    const label = booking?.art === 'WARM_MIETE' ? VAT_OBJEKT_MODEL_COPY.warmmieteOhneAufteilung : kontoLabel(konto);
    addTo(map, `${konto}|${label}`, {konto, label, anrechenbarLabel}, line.gross, line.net, line.tax, line.tax);
};

const addCost = (byRate: Map<VatRate, Map<string, RowAccumulator>>, input: ObjektAbrechnungInput, line: VatLine): void => {
    const map = rateMap(byRate, line.rate);
    const expense = input.expensesByID[line.source.id] ?? null;
    const accountRefID = expense?.accountRefID ?? null;
    const konto = accountRefID ?? VAT_OBJEKT_MODEL_COPY.keinKonto;
    const label = expense?.accountLabel ?? (accountRefID ? kontoLabel(accountRefID) : VAT_OBJEKT_MODEL_COPY.ohneBuchungskonto);

    if (line.rule !== 'VORSTEUER') {
        // Nachtrag § 17 auf die Ausgabe (minderung.ts buildAdjustmentLines: rule ADJUSTMENT, gross = tax = delta, net 0, share 1).
        // Die Zeile IST die Korrektur – der volle Rechnungsbetrag steht bereits in der VORSTEUER-Zeile derselben Ausgabe und
        // darf nicht erneut gezählt werden → Zeilenwerte, eigener Anrechenbar-Text und eigener Schlüssel (kein Verschmelzen
        // mit „100 %“ bei voll abziehbar).
        const anrechenbarLabel = VAT_OBJEKT_MODEL_COPY.anrechenbar.nachtrag;
        addTo(map, `${konto}|${label}|NACHTRAG`, {konto, label, anrechenbarLabel}, line.gross, line.net, line.tax, line.tax);
        return;
    }

    const anrechenbarLabel = anrechenbarLabelFor(line);
    // Engine-Semantik (line.ts buildLine): gross/tax der Zeile sind BEREITS mit dem Anteil skaliert → die Zeilensteuer ist die
    // anrechenbare Vorsteuer. Brutto/Netto/USt zeigen den vollen Rechnungsbetrag aus der Ausgabe (Fallback: Zeile ÷ Anteil).
    const full = fullExpenseAmounts(expense, line);
    addTo(map, `${konto}|${label}|${anrechenbarLabel}`, {konto, label, anrechenbarLabel}, full.brutto, full.netto, full.ust, line.tax);
};

/** Spiegel von `isFiniteNumber` (vorsteuer.ts): `null` explizit ausschließen – `Number(null)` wäre 0 und damit „endlich“. */
const finiteOrNull = (value: number | null | undefined): number | null =>
    value !== null && value !== undefined && Number.isFinite(Number(value)) ? Number(value) : null;

/**
 * Voller Rechnungsbetrag einer Kostenzeile (nur Regel VORSTEUER): aus der Ausgabe (Betrag/USt-Betrag/Satz), sonst Zeile ÷ Anteil.
 * Vorzeichen wie die Engine (vorsteuer.ts): bei vorhandener Ausgabe entscheidet allein `type === 'CREDIT'` – Nahaus speichert
 * Beträge negativ, und bei share 0 („kein Abzug“) trägt die Zeile kein Vorzeichen mehr (gross = tax = 0). Ohne Ausgabe bleibt
 * das Vorzeichen der Zeile erhalten (Division durch den Anteil ändert es nicht).
 */
const fullExpenseAmounts = (expense: ObjektExpenseLite | null, line: VatLine): { brutto: number; netto: number; ust: number } => {
    const amount = expense ? finiteOrNull(expense.amount) : null;
    if (expense && amount !== null) {
        const sign = expense.type === 'CREDIT' ? -1 : 1;
        const abs = Math.abs(amount);
        // USt-Betrag exakt wie vorsteuer.ts `vatFull`: ein endlicher `vatAmount` gewinnt – AUCH 0 (dann ist auch line.tax 0 und
        // USt × Anteil = anrechenbare USt geht auf); nur null → aus dem Satz der Zeile herleiten. `toExpenseLite`
        // (services/vat-lite.adapters.ts) reicht `vatAmount` per `finiteOrNull` unverändert durch, 0 kommt also an; die
        // Ausgaben-Formulare schreiben das Feld heute nicht (nur Interface + Mock), es stammt aus Import/API/Altdaten.
        const vatAmount = finiteOrNull(expense.vatAmount);
        // Eine VORSTEUER-Zeile existiert nur bei USt-pflichtigen Ausgaben (Engine filtert includesVAT === false vorab) → Satz der Zeile gilt.
        const ust = line.rate === 0 ? 0 : (vatAmount !== null ? Math.abs(vatAmount) : round2(abs - round2(abs / (1 + line.rate / 100))));
        const brutto = round2(sign * abs);
        return {brutto, ust: round2(sign * ust), netto: round2(brutto - round2(sign * ust))};
    }
    if (line.share > 0 && line.share < 1) {
        const brutto = round2(line.gross / line.share);
        const ust = round2(line.tax / line.share);
        return {brutto, ust, netto: round2(brutto - ust)};
    }
    return {brutto: line.gross, netto: line.net, ust: line.tax};
};

const toNonTaxableRow = (line: VatLine): ObjektKontoRow => ({
    konto: VAT_OBJEKT_MODEL_COPY.keinKonto,
    label: line.label,
    brutto: round2(line.gross),
    netto: round2(line.net),
    ust: round2(line.tax),
    anrechenbarLabel: VAT_OBJEKT_MODEL_COPY.anrechenbar.nichtSteuerbar,
    anrechenbareUst: 0,
    lineCount: 1,
});

export const buildObjektAbrechnung = (input: ObjektAbrechnungInput): ObjektAbrechnungModel => {
    const lines = input.lines.filter(line => line.source.propertyID === input.property.id);
    const ertraegeByRate = new Map<VatRate, Map<string, RowAccumulator>>();
    const kostenByRate = new Map<VatRate, Map<string, RowAccumulator>>();
    const nichtSteuerbar: ObjektKontoRow[] = [];

    for (const line of lines) {
        if (isNonTaxableLine(line)) {
            nichtSteuerbar.push(toNonTaxableRow(line));
        } else if (isCostLine(line)) {
            addCost(kostenByRate, input, line);
        } else if (isRevenueLine(line)) {
            addRevenue(ertraegeByRate, input, line);
        }
    }

    const ertraege = toRateGroups(ertraegeByRate);
    const kosten = toRateGroups(kostenByRate);
    nichtSteuerbar.sort((a, b) => a.label.localeCompare(b.label, 'de'));

    const ertraegeUst = round2(ertraege.reduce((a, g) => a + g.sum.ust, 0));
    const kostenAnrechenbar = round2(kosten.reduce((a, g) => a + g.sum.anrechenbareUst, 0));
    return {
        ertraege,
        kosten,
        nichtSteuerbar,
        totals: {ertraegeUst, kostenAnrechenbar, saldo: round2(ertraegeUst - kostenAnrechenbar)},
    };
};
